import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { trackEvent } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const [result, setResult] = useState({ type: "booking", credits: 0 });

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempt) => {
    if (attempt >= 6) { setStatus("timeout"); return; }
    try {
      const res = await axios.get(`${API}/payments/status/${sid}`);
      if (res.data.payment_status === "paid") {
        // Purchase event for GTM — once-per-session guard so polling/refresh doesn't double-fire
        if (!sessionStorage.getItem(`purchase_sent_${sid}`)) {
          const cents = res.data.amount_total || 0;
          // Stripe returns amount in cents
          const value = cents > 0 ? cents / 100 : 0;
          trackEvent("purchase", {
            value,
            currency: (res.data.currency || "aud").toUpperCase(),
            content_type: res.data.type === "credit_pack" ? "credit_pack" : "product",
            content_ids: [sid],
          });
          sessionStorage.setItem(`purchase_sent_${sid}`, "1");
        }
        setResult({ type: res.data.type || "booking", credits: res.data.credits || 0 });
        setStatus("success");
      } else if (res.data.status === "expired") {
        setStatus("expired");
      } else {
        setAttempts(attempt + 1);
        setTimeout(() => pollPaymentStatus(sid, attempt + 1), 2500);
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="payment-success-page">
      <Navbar />
      <div className="pt-32 pb-16 px-6 flex justify-center">
        <div className="max-w-md w-full text-center">
          {status === "checking" && (
            <div className="animate-fade-in" data-testid="payment-checking">
              <Loader2 className="h-16 w-16 animate-spin text-brand-purple mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-2">Verifying Payment</h1>
              <p className="text-slate-500 dark:text-slate-400 font-body">Please wait while we confirm your payment...</p>
              <p className="text-xs text-slate-400 font-body mt-2">Attempt {attempts + 1} of 6</p>
            </div>
          )}
          {status === "success" && (
            <div className="animate-fade-in-up" data-testid="payment-success">
              <div className="bg-green-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              </div>
              {result.type === "weekly_subscription" ? (
                <>
                  <h1 className="font-heading text-3xl font-bold text-slate-900 dark:text-white mb-3" data-testid="subscription-success-title">
                    Weekly subscription active!
                  </h1>
                  <p className="text-slate-500 dark:text-slate-400 font-body mb-2">+1 credit has been added to your account.</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 font-body mb-8">Your card will be charged $59 every week. A new credit lands in your account on each successful payment. Cancel anytime from your dashboard.</p>
                  <Button onClick={() => navigate("/member/dashboard")}
                    className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full px-8 py-3 font-body font-semibold"
                    data-testid="back-to-dashboard-btn">
                    Back to dashboard
                  </Button>
                </>
              ) : result.type === "credit_pack" ? (
                <>
                  <h1 className="font-heading text-3xl font-bold text-slate-900 dark:text-white mb-3" data-testid="credit-pack-success-title">
                    {result.credits} credit{result.credits !== 1 ? "s" : ""} added!
                  </h1>
                  <p className="text-slate-500 dark:text-slate-400 font-body mb-2">Your account has been topped up.</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 font-body mb-8">Credits stay active as long as you book a class within 10 weeks.</p>
                  <Button onClick={() => navigate("/member/dashboard")}
                    className="bg-brand-gold hover:bg-brand-gold-hover text-white rounded-full px-8 py-3 font-body font-semibold"
                    data-testid="back-to-dashboard-btn">
                    Back to dashboard
                  </Button>
                </>
              ) : (
                <>
                  <h1 className="font-heading text-3xl font-bold text-slate-900 dark:text-white mb-3">Booking Confirmed!</h1>
                  <p className="text-slate-500 dark:text-slate-400 font-body mb-2">Your payment was successful.</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 font-body mb-8">A confirmation email with your class details and Google Meet link has been sent to your email address.</p>
                  <Button onClick={() => navigate("/")}
                    className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full px-8 py-3 font-body font-semibold"
                    data-testid="back-to-home-btn">
                    Back to Home
                  </Button>
                </>
              )}
            </div>
          )}
          {(status === "error" || status === "timeout" || status === "expired") && (
            <div className="animate-fade-in" data-testid="payment-error">
              <div className="bg-red-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-red-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">
                {status === "expired" ? "Payment Expired" : status === "timeout" ? "Verification Timeout" : "Something Went Wrong"}
              </h1>
              <p className="text-slate-500 font-body mb-8">
                {status === "expired" ? "Your payment session has expired." : status === "timeout" ? "Check your email for confirmation." : "We couldn't verify your payment."}
              </p>
              <Button onClick={() => navigate("/")}
                className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full px-8 py-3 font-body font-semibold"
                data-testid="return-home-btn">
                Return Home
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
